/*
 * "Retro": el director deja retroalimentacion para el jefe zonal (4 campos: fortalezas, oportunidades,
 * compromisos, meta) y para cada tienda de la zona (comentario libre), agrupadas por color de IGI
 * (debil/media/fuerte/sin evaluar) - replica 1:1 la pantalla real de produccion (igi.logcomec.com),
 * donde el selector solo lista zonas (no hay opcion de tienda suelta). Los datos de las tiendas
 * (e1/e2/e3/igi/color) salen del mismo GET /igi/resumen/ que ya usaba esta pantalla para el selector
 * (mismo dato que "Mi zona", no se pide dos veces). El formulario para escribir solo aparece con
 * igi_write_retro (prop, mismo patron que puedeEvaluar en tienda/index.js); cualquier persona con
 * permiso de ver IGI ve el historial de la zona (retroalimentacion recibida).
 *
 * Un solo boton "Guardar retroalimentacion" guarda todo: dispara create_retro para la zona (si algun
 * campo tiene texto) y una vez por cada tienda con comentario, todas contra el mismo POST /igi/retro/
 * que ya existia (acepta zona XOR tienda, nunca se toco el backend). Las llamadas van SECUENCIALES
 * (await una por una, no en paralelo): create_retro despacha su resultado a un slice compartido
 * (_create_retro) que una sola peticion en vuelo a la vez puede leer sin que otra se lo pise - el mismo
 * tipo de bug que tuvo que resolverse con una "clave" en Trafico/ERP, evitado aqui simplemente por no
 * lanzar las peticiones en paralelo.
 */
import React, {useCallback, useEffect, useRef, useState} from "react";
import {useDispatch, useSelector, useStore} from "react-redux";
import {toast} from "react-toastify";
import Breadcrumbs from "../../../../components/breadcrumbs";
import Button from "../../../../components/button";
import {
    clear_igi, create_retro, get_igi_resumen, get_retro,
    CREATE_RETRO_SUCCESS, GET_IGI_RESUMEN_SUCCESS, GET_RETRO_SUCCESS,
} from "../../../../redux/igi";
import {formatoFecha, formatoNota, estilo3} from "../niveles";

const GRUPOS = [
    {color: "debil", emoji: "🔴", titulo: "Débiles", leyenda: "plan de acción"},
    {color: "media", emoji: "🟡", titulo: "Medias", leyenda: "qué reforzar"},
    {color: "fuerte", emoji: "🟢", titulo: "Fuertes", leyenda: "felicitación"},
    {color: null, emoji: "⬜", titulo: "Sin evaluar", leyenda: ""},
];

const EJES = [
    {clave: "e1", etiqueta: "Cap. Interna"},
    {clave: "e2", etiqueta: "Cond. Externas"},
    {clave: "e3", etiqueta: "Tareas Oper."},
];

const CAMPOS_ZONA = [
    {clave: "fortalezas", etiqueta: "Fortalezas que reconozco", placeholder: "Lo que está haciendo bien..."},
    {clave: "oportunidades", etiqueta: "Oportunidades de mejora", placeholder: "Áreas donde debe mejorar..."},
    {clave: "compromisos", etiqueta: "Compromisos que le pido", placeholder: "Acciones concretas que debe tomar..."},
    {clave: "meta", etiqueta: "Meta IGI para el próximo mes", placeholder: "Ej: Subir de 3.2 a 3.6"},
];

const CAMPOS_ZONA_VACIOS = {fortalezas: "", oportunidades: "", compromisos: "", meta: ""};

const componerTextoZona = (campos) => CAMPOS_ZONA
    .filter((c) => campos[c.clave] && campos[c.clave].trim())
    .map((c) => `${c.etiqueta}:\n${campos[c.clave].trim()}`)
    .join("\n\n");

const Retro = (props) => {
    const dispatch = useDispatch();
    const store = useStore();
    const {token} = useSelector((state) => state.users_module.login.data) || null;
    const resumen = useSelector((state) => state.igi_module._get_resumen);
    const puedeEscribir = Object.values(props).some((x) => x && x.codigo === "igi_write_retro");

    const [pedidoResumen, setPedidoResumen] = useState(false);
    const [zonaId, setZonaId] = useState(null);
    const [camposZona, setCamposZona] = useState(CAMPOS_ZONA_VACIOS);
    const [comentariosTienda, setComentariosTienda] = useState({});
    const [guardando, setGuardando] = useState(false);
    // El historial que se muestra vive en estado local, no se lee directamente del slice de redux: si
    // el usuario cambia de zona rapido pueden quedar dos peticiones en vuelo a la vez y resolver fuera
    // de orden. secuenciaRef numera cada peticion; si al resolver una peticion su numero ya no es el
    // ultimo emitido, es una respuesta vieja y se descarta en silencio.
    const secuenciaRef = useRef(0);
    const [historialMostrado, setHistorialMostrado] = useState({datos: [], status: 0, message: ""});

    useEffect(() => {
        dispatch(clear_igi(GET_IGI_RESUMEN_SUCCESS));
        dispatch(clear_igi(CREATE_RETRO_SUCCESS));
        setPedidoResumen(true);
        dispatch(get_igi_resumen(token));
    }, [dispatch, token]);

    const zonas = resumen.data && resumen.data.datos ? resumen.data.datos : [];
    const cargandoResumen = pedidoResumen && resumen.status === 0;
    const errorResumen = resumen.status !== 0 && resumen.status !== 200
        ? (resumen.message || "No se pudo cargar el resumen") : null;
    const zona = zonas.find((z) => z.id === zonaId) || null;

    const cargarHistorial = useCallback((id) => {
        const miSecuencia = ++secuenciaRef.current;
        dispatch(clear_igi(GET_RETRO_SUCCESS));
        Promise.resolve(dispatch(get_retro(token, {zona: id}))).then(() => {
            if (miSecuencia !== secuenciaRef.current) {
                return; // se elegio otra zona despues: esta respuesta ya quedo obsoleta
            }
            const resultado = store.getState().igi_module._get_retro;
            setHistorialMostrado({
                datos: resultado.data && resultado.data.datos ? resultado.data.datos : [],
                status: resultado.status,
                message: resultado.message,
            });
        });
    }, [dispatch, token, store]);

    const elegirZona = (id) => {
        setZonaId(id);
        setCamposZona(CAMPOS_ZONA_VACIOS);
        setComentariosTienda({});
        setHistorialMostrado({datos: [], status: 0, message: ""});
        cargarHistorial(id);
    };

    const grupos = zona ? GRUPOS
        .map((g) => ({...g, tiendas: zona.tiendas.filter((t) => t.color === g.color)}))
        .filter((g) => g.tiendas.length > 0) : [];

    const guardar = async (e) => {
        e.preventDefault();
        const textoZona = componerTextoZona(camposZona);
        const comentarios = Object.entries(comentariosTienda).filter(([, texto]) => texto && texto.trim());
        if (!textoZona && comentarios.length === 0) {
            toast.error("Escriba algo para guardar");
            return;
        }
        setGuardando(true);
        const fallidos = [];
        if (textoZona) {
            await dispatch(create_retro(token, {zona: zonaId, texto: textoZona}));
            const resultado = store.getState().igi_module._create_retro;
            if (resultado.status !== 201) {
                fallidos.push(`Zona: ${resultado.message || "no se pudo guardar"}`);
            }
        }
        for (const [tiendaId, texto] of comentarios) {
            await dispatch(create_retro(token, {tienda: Number(tiendaId), texto: texto.trim()}));
            const resultado = store.getState().igi_module._create_retro;
            if (resultado.status !== 201) {
                const tienda = zona.tiendas.find((t) => t.id === Number(tiendaId));
                fallidos.push(`${tienda ? tienda.nombre : tiendaId}: ${resultado.message || "no se pudo guardar"}`);
            }
        }
        dispatch(clear_igi(CREATE_RETRO_SUCCESS));
        setGuardando(false);
        if (fallidos.length === 0) {
            toast.success("Retroalimentación guardada");
            setCamposZona(CAMPOS_ZONA_VACIOS);
            setComentariosTienda({});
            cargarHistorial(zonaId);
        } else {
            toast.error(`No se pudo guardar: ${fallidos.join("; ")}`);
        }
    };

    return (
        <div className="app-content content">
            <div className="content-overlay"/>
            <div className="content-wrapper">
                <div className="content-header row">
                    <div className="content-header-left col-12 mb-2 mt-1">
                        <Breadcrumbs title="Retro" items={[]}/>
                    </div>
                </div>
                <div className="content-body">
                    {cargandoResumen && (
                        <div className="alert alert-info" role="status" aria-busy="true">
                            <i className="bx bx-loader-alt bx-spin mr-50"/> Cargando zonas…
                        </div>
                    )}
                    {errorResumen && <div className="alert alert-danger">{errorResumen}</div>}
                    <div className="form-group">
                        <label htmlFor="retro-zona">Zona</label>
                        <select
                            id="retro-zona"
                            className="form-control"
                            value={zonaId || ""}
                            onChange={(e) => elegirZona(parseInt(e.target.value, 10))}
                        >
                            <option value="" disabled>Seleccione...</option>
                            {zonas.map((z) => (
                                <option key={z.id} value={z.id}>
                                    {z.nombre} — {z.jefe.nombres || z.jefe.usuario}
                                    {z.igi_promedio !== null ? ` (IGI ${formatoNota(z.igi_promedio)})` : ""}
                                </option>
                            ))}
                        </select>
                    </div>

                    {zona && (
                        <form onSubmit={guardar}>
                            <div className="card mb-1">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <h5 className="mb-0">
                                            {zona.nombre} <small className="text-muted">{zona.jefe.nombres || zona.jefe.usuario}</small>
                                        </h5>
                                        {zona.igi_promedio !== null && (
                                            <span className="badge" style={{...estilo3(zona.igi_promedio), fontWeight: 600}}>
                                                IGI {formatoNota(zona.igi_promedio)}
                                            </span>
                                        )}
                                    </div>
                                    {puedeEscribir ? (
                                        <div className="row">
                                            {CAMPOS_ZONA.map((c) => (
                                                <div className="col-md-6 form-group" key={c.clave}>
                                                    <label htmlFor={`retro-${c.clave}`}>{c.etiqueta}</label>
                                                    <textarea
                                                        id={`retro-${c.clave}`}
                                                        className="form-control"
                                                        rows={3}
                                                        placeholder={c.placeholder}
                                                        value={camposZona[c.clave]}
                                                        onChange={(e) => setCamposZona({...camposZona, [c.clave]: e.target.value})}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    ) : null}
                                </div>
                            </div>

                            {puedeEscribir && grupos.length > 0 && (
                                <div className="mb-1">
                                    <h5>Retroalimentación por joyería</h5>
                                    {grupos.map((g) => (
                                        <div key={g.titulo} className="mb-1">
                                            <h6>
                                                {g.emoji} {g.titulo}{g.leyenda ? ` — ${g.leyenda}` : ""} ({g.tiendas.length})
                                            </h6>
                                            <div className="row">
                                                {g.tiendas.map((t) => (
                                                    <div className="col-md-6 mb-1" key={t.id}>
                                                        <div className="card h-100">
                                                            <div className="card-body">
                                                                <div className="d-flex justify-content-between">
                                                                    <div>
                                                                        <strong>{t.nombre}</strong>
                                                                        <div className="small text-muted">
                                                                            {t.codigo} · IGI {formatoNota(t.igi)}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="mt-50 mb-50">
                                                                    {EJES.map((eje) => (
                                                                        <span
                                                                            key={eje.clave}
                                                                            className="badge d-block mb-25 text-left"
                                                                            style={{...estilo3(t[eje.clave]), fontWeight: 600}}
                                                                        >
                                                                            {eje.etiqueta} {formatoNota(t[eje.clave])}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                                <textarea
                                                                    className="form-control"
                                                                    rows={2}
                                                                    placeholder="Qué debe reforzar esta joyería..."
                                                                    value={comentariosTienda[t.id] || ""}
                                                                    onChange={(e) => setComentariosTienda({
                                                                        ...comentariosTienda, [t.id]: e.target.value,
                                                                    })}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {puedeEscribir && (
                                <Button type="submit" theme="success" disabled={guardando} text="Guardar retroalimentación"/>
                            )}
                        </form>
                    )}

                    {zona && (
                        <div className="mt-2">
                            <h5>Historial de {zona.nombre}</h5>
                            {historialMostrado.status === 0 && (
                                <div className="alert alert-info" role="status" aria-busy="true">
                                    <i className="bx bx-loader-alt bx-spin mr-50"/> Cargando historial…
                                </div>
                            )}
                            {historialMostrado.status !== 0 && historialMostrado.status !== 200 && (
                                <div className="alert alert-danger">
                                    {historialMostrado.message || "No se pudo cargar el historial"}
                                </div>
                            )}
                            {historialMostrado.status === 200 && historialMostrado.datos.length === 0 && (
                                <div className="alert alert-info">Sin retroalimentación registrada.</div>
                            )}
                            {historialMostrado.datos.map((r) => (
                                <div key={r.id} className="card mb-1">
                                    <div className="card-body p-1">
                                        <div className="small text-muted">
                                            {formatoFecha(r.fecha)} — {r.autor.nombres || r.autor.usuario}
                                        </div>
                                        <p className="mb-0" style={{whiteSpace: "pre-wrap"}}>{r.texto}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Retro;
